import React from 'react';
import { Check } from 'lucide-react';
import type { RenfortPropose } from '../../../lib/planningExcelImport/types';
import { renfortComplet } from '../../../lib/planningExcelImport/construireSemaine';

/**
 * Les renforts extérieurs relevés dans le fichier, proposés à l'étape 3 avant l'écriture.
 *
 * Rien n'est deviné : chaque ligne est préremplie depuis le texte (« 1 SECURITE 18H30-02H »), reste
 * modifiable, et n'est créée dans le planning que si sa case est cochée. Une ligne dont l'horaire manque
 * ne peut pas être cochée tant qu'il n'est pas saisi.
 */
const CHAMP = 'min-w-0 px-2 py-1 rounded-[6px] border border-border bg-surface text-xs text-content';

function jourCourt(date: string): string {
    const d = new Date(`${date}T00:00:00`);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

export function RenfortsAConfirmer({ renforts, typesCatalogue, onChange }: {
    renforts: RenfortPropose[];
    /** Les types de renfort du catalogue Extras Partenaires, tels que le planning les propose à la main. */
    typesCatalogue: string[];
    onChange: (index: number, patch: Partial<RenfortPropose>) => void;
}) {
    if (!renforts.length) return null;
    const confirmes = renforts.filter((r) => r.confirme).length;

    return (
        <div className="border border-border rounded-[10px] overflow-hidden">
            <div className="px-[13px] py-2 bg-surface-soft border-b border-border">
                <div className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">
                    Renforts extérieurs relevés ({renforts.length}) — {confirmes ? `${confirmes} à créer` : 'aucun coché'}
                </div>
                <p className="mt-1 text-[11px] leading-snug text-content-muted text-pretty">
                    Ils ne deviennent jamais des créneaux de salarié. Cochez ceux à créer en renfort dans le planning,
                    après avoir vérifié l'effectif et l'horaire ; les autres restent notés sur la journée.
                </p>
            </div>
            <div className="max-h-[220px] overflow-y-auto">
                {renforts.map((renfort) => {
                    const complet = renfortComplet(renfort);
                    const libelleHorsCatalogue = renfort.label && !typesCatalogue.includes(renfort.label);
                    return (
                        <div key={renfort.index} className={`px-[13px] py-2 border-b border-border/60 last:border-b-0 ${renfort.confirme ? 'bg-state-ok-soft/40' : ''}`}>
                            <label className={`flex items-center gap-2 ${complet ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={renfort.confirme}
                                    disabled={!complet}
                                    onChange={() => onChange(renfort.index, { confirme: !renfort.confirme })}
                                />
                                <span className={`w-[17px] h-[17px] shrink-0 rounded-[5px] border-[1.5px] flex items-center justify-center ${renfort.confirme ? 'bg-content border-content text-surface' : 'bg-surface border-border'} ${complet ? '' : 'opacity-40'}`}>
                                    {renfort.confirme && <Check size={11} strokeWidth={3} />}
                                </span>
                                <span className="font-mono text-[11px] text-content-muted shrink-0 capitalize">{jourCourt(renfort.date)}</span>
                                <span className="text-[11px] min-w-0 truncate text-content-muted" title={renfort.texte}>« {renfort.texte} »</span>
                                {!complet && <span className="ml-auto shrink-0 text-[10px] font-bold text-state-warn whitespace-nowrap">horaire à saisir</span>}
                            </label>
                            <div className="mt-1.5 grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-[7px] pl-[25px]">
                                <input
                                    type="number"
                                    min={1}
                                    value={renfort.effectif}
                                    onChange={(e) => onChange(renfort.index, { effectif: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                                    className={`${CHAMP} text-center tabular-nums`}
                                    aria-label="Effectif"
                                />
                                <select
                                    value={renfort.label}
                                    onChange={(e) => onChange(renfort.index, { label: e.target.value })}
                                    className={`${CHAMP} font-semibold`}
                                    aria-label="Type de renfort"
                                >
                                    {libelleHorsCatalogue && <option value={renfort.label}>{renfort.label} (tel qu'écrit)</option>}
                                    {typesCatalogue.map((type) => <option key={type} value={type}>{type}</option>)}
                                </select>
                                <span className="flex items-center gap-1 shrink-0">
                                    <input
                                        type="time"
                                        value={renfort.debut}
                                        onChange={(e) => onChange(renfort.index, { debut: e.target.value })}
                                        className={`${CHAMP} tabular-nums`}
                                        aria-label="Début"
                                    />
                                    <span className="text-[11px] text-content-muted">→</span>
                                    <input
                                        type="time"
                                        value={renfort.fin}
                                        onChange={(e) => onChange(renfort.index, { fin: e.target.value })}
                                        className={`${CHAMP} tabular-nums`}
                                        aria-label="Fin"
                                    />
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
